import React, { StrictMode, useEffect } from "react";
import { Provider } from "react-redux";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import "@minimum.eco/min-ui";
import * as Sentry from "@sentry/react";
import { Init } from "minimum-ui/init";
import { setAxiosInterceptors } from "net/inteceptors";
import MaintenancePage from "pages/MaintenancePage";
import { createRoot } from "react-dom/client";
import { StyleSheetManager } from "styled-components";
import { GlobalVariableStyles } from "theme/vars";
import FontFamilyDeclaraton from "typography/install";

import config from "config";

import { store } from "store/store";

import ScrollToTop from "utilities/hooks/scrollToTop";
import { isDevelopment } from "utilities/utils";

import { BusyBackdrop } from "Common";

import App from "./App";
import GlobalStyles from "./GlobalStyle";
import "./index.scss";
import initializeI18n from "./localization";
import reportWebVitals from "./reportWebVitals";

setAxiosInterceptors();
void initializeI18n();

if (!isDevelopment) {
  Sentry.init({
    dsn: config.sentry.dsn,
    environment: config.environment,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    normalizeDepth: 10,
    tracesSampleRate: 1,
    release: `${config.app.name}@${config.app.sem_ver}`,
    beforeSend(event, hint) {
      const originalException = hint?.originalException;
      const message =
        originalException instanceof Error ? originalException.message : "";

      // Don't log 404s to Sentry - they're not bugs.
      if (/request failed with status code 404/i.test(message)) {
        return null;
      }

      return event;
    },
  });
}

const StyleSheets = React.memo(() => {
  return (
    <StyleSheetManager target={document.body}>
      <>
        <GlobalStyles />
        <FontFamilyDeclaraton />
        <GlobalVariableStyles />
      </>
    </StyleSheetManager>
  );
});

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <StrictMode>
    <Init>
      <StyleSheets />
      {process.env.MAINTENANCE === "on" && <MaintenancePage />}
      {process.env.MAINTENANCE !== "on" && (
        <Provider store={store}>
          <BrowserRouter>
            <ScrollToTop />
            <App />
          </BrowserRouter>
        </Provider>
      )}
      <BusyBackdrop />
    </Init>
  </StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
